<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Navbar -->
    <nav class="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20">
          <button @click="goToHome" class="flex items-center hover:opacity-80 transition-opacity cursor-pointer">
            <img
              :src="logoUrl"
              alt="TripDay - Gestão de Hospedagens"
              class="h-12 md:h-14 w-auto"
            />
          </button>

          <div class="flex items-center gap-2 sm:gap-6">
            <button
              @click="goToShowcase"
              class="inline-flex text-xs sm:text-sm font-medium text-gray-700 hover:text-indigo-700 hover:bg-indigo-50 px-2 sm:px-3 py-2 rounded-lg transition whitespace-nowrap"
            >
              Vitrine
            </button>

            <button
              @click="goToAbout"
              class="inline-flex text-xs sm:text-sm font-medium text-gray-700 hover:text-indigo-700 hover:bg-indigo-50 px-2 sm:px-3 py-2 rounded-lg transition whitespace-nowrap"
            >
              Quem Somos
            </button>

            <!-- Área do anfitrião (dropdown simples) -->
            <div class="relative">
            <button
              @click="toggleHostMenu"
              class="flex items-center gap-1 sm:gap-2 text-indigo-700 font-semibold px-2 sm:px-4 py-2 rounded-lg hover:bg-indigo-50 transition whitespace-nowrap text-xs sm:text-base"
            >
              <span class="hidden sm:inline">Área do Anfitrião</span>
              <span class="sm:hidden">Anfitrião</span>
              <svg
                class="w-4 h-4 transition-transform"
                :class="{ 'rotate-180': isHostMenuOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <div
                v-if="isHostMenuOpen"
                class="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg py-2 z-10 border border-gray-100"
              >
                <p class="text-center px-4 pb-2 text-[12px] text-orange-500 border-b border-gray-100">
                  Área exclusiva para anfitriões e imobiliárias
                </p>
                <button
                  @click="openHostLogin"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition"
                >
                  Entrar
                </button>
                <button
                  @click="openHostRegister"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition"
                >
                  Criar Conta
                </button>
              </div>
            </transition>
          </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Property Showcase / Quem Somos (URL: /tenant e /tenant/about) -->
    <PropertyShowcase v-if="landingSection === 'showcase'" />
    <About v-else />

    <!-- Login Modal -->
    <div v-if="showLoginModal" class="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
      <div class="relative">
        <button @click="activeModal = null" class="absolute -top-10 right-0 text-white font-bold text-lg hover:text-gray-200">
          ✕ Fechar
        </button>
        <TenantLogin 
          key="login"
          @login-success="handleLoginSuccess"
          @switch-to-register="switchToRegister"
          @go-back="closeAuthModal"
        />
      </div>
    </div>

    <!-- Register Modal -->
    <div v-if="showRegisterModal" class="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
      <div class="relative">
        <button @click="activeModal = null" class="absolute -top-10 right-0 text-white font-bold text-lg hover:text-gray-200">
          ✕ Fechar
        </button>
        <TenantRegister 
          key="register"
          @register-success="handleRegisterSuccess"
          @switch-to-login="switchToLogin"
          @go-back="closeAuthModal"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TenantLogin from './TenantLogin.vue'
import TenantRegister from './TenantRegister.vue'
import PropertyShowcase from './PropertyShowcase.vue'
import About from './About.vue'
import { navigateTo } from '../router'

export default {
  components: { TenantLogin, TenantRegister, PropertyShowcase, About },
  emits: ['auth-success'],

  props: {
    landingSection: {
      type: String,
      default: 'showcase',
      validator: (v) => ['showcase', 'about'].includes(v)
    }
  },
  
  data() {
    return {
      logoUrl: '/tripday-logo.png',
      activeModal: null, // 'login', 'register', ou null
      isHostMenuOpen: false
    }
  },
  
  computed: {
    showLoginModal() {
      return this.activeModal === 'login'
    },
    showRegisterModal() {
      return this.activeModal === 'register'
    }
  },

  methods: {
    handleLoginSuccess(data) {
      const masterCode = data.tenant.master_code
      window.history.replaceState({}, document.title, `/${masterCode}`)
      this.$emit('auth-success', data)
    },

    handleRegisterSuccess(data) {
      const masterCode = data.tenant.master_code
      window.history.replaceState({}, document.title, `/${masterCode}`)
      // Tenant inativo: não faz login, só redireciona para a vitrine dele (mostra "pendente de homologação")
      if (data.tenant.active === false || (!data.tenantToken && !data.userToken)) {
        this.closeAuthModal()
        window.location.reload()
        return
      }
      this.$emit('auth-success', data)
    },

    switchToRegister() {
      this.activeModal = 'register'
      this.isHostMenuOpen = false
    },

    switchToLogin() {
      this.activeModal = 'login'
      this.isHostMenuOpen = false
    },

    goToHome() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('dashboard')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    closeAuthModal() {
      this.activeModal = null
      this.isHostMenuOpen = false
    },

    toggleHostMenu() {
      this.isHostMenuOpen = !this.isHostMenuOpen
    },

    openHostLogin() {
      this.activeModal = 'login'
      this.isHostMenuOpen = false
    },

    openHostRegister() {
      this.activeModal = 'register'
      this.isHostMenuOpen = false
    },

    goToShowcase() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('dashboard')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    goToAbout() {
      this.activeModal = null
      this.isHostMenuOpen = false
      navigateTo('about')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>
